/**
 * Shared Platform Action registry — the first convergence point for the
 * command palette (manual search/command input) and Alice (conversational /
 * contextual command layer). Both surfaces are intended to read from one set
 * of action definitions so behavior never drifts between them.
 *
 * This is intentionally *separate* from `@command-center/action-sdk`:
 *   - `action-sdk` describes server-side execution (handlers, connectors,
 *     confirm, logging) for write actions that run on the Worker.
 *   - `PlatformAction` (here) is a lightweight, frontend-safe descriptor used
 *     to render and route actions in the UI. A `PlatformAction` with
 *     `handlerType: 'server'` ultimately delegates to an `action-sdk` action.
 *
 * Frontend-safe: descriptors + pure helpers only. No secrets, no fetching, no
 * direct record access. Permission/approval enforcement is server-side.
 */

import type { AliceContextSourceType, PortalAliceScope } from './alice';

export type PlatformActionCategory =
  | 'navigation'
  | 'draft'
  | 'record'
  | 'workflow'
  | 'ai'
  | 'utility';

export type PlatformActionScope = 'global' | 'page' | 'record' | 'portal';

export type PlatformActionHandlerType =
  | 'frontend'
  | 'server'
  | 'ai'
  | 'placeholder';

export interface PlatformAction {
  key: string;
  label: string;
  description?: string;
  /** Optional icon name (lucide) for UI surfaces. */
  icon?: string;
  category: PlatformActionCategory;
  scope: PlatformActionScope;
  /** Portal scopes allowed to use this action (omit = all scopes). */
  allowedPortalScopes?: PortalAliceScope[];
  /** Context record types this action needs to be available (omit = none). */
  requiredContextTypes?: AliceContextSourceType[];
  requiresApproval?: boolean;
  startsComposerSession?: boolean;
  handlerType: PlatformActionHandlerType;
  /** Optional command-palette trigger string (e.g. "/goto tasks"). */
  paletteCommand?: string;
  /** Optional route for navigation actions. */
  route?: string;
  /** Marks an action whose handler is not implemented yet. */
  placeholder?: boolean;
}

export interface PlatformActionRegistry {
  all(): PlatformAction[];
  get(key: string): PlatformAction | undefined;
  register(action: PlatformAction): PlatformAction;
  /** Actions visible in a given portal scope. */
  forPortalScope(scope: PortalAliceScope): PlatformAction[];
  /** Actions whose required context types are satisfied by `available`. */
  forContext(available: AliceContextSourceType[]): PlatformAction[];
  /** Actions in a given category. */
  byCategory(category: PlatformActionCategory): PlatformAction[];
}

function isAllowedInScope(action: PlatformAction, scope: PortalAliceScope): boolean {
  return !action.allowedPortalScopes || action.allowedPortalScopes.includes(scope);
}

export function createPlatformActionRegistry(
  actions: PlatformAction[] = [],
): PlatformActionRegistry {
  const entries = new Map<string, PlatformAction>();
  for (const action of actions) {
    if (entries.has(action.key)) {
      throw new Error(`Platform action already registered: ${action.key}`);
    }
    entries.set(action.key, action);
  }

  return {
    all: () => Array.from(entries.values()),
    get: (key) => entries.get(key),
    register: (action) => {
      if (entries.has(action.key)) {
        throw new Error(`Platform action already registered: ${action.key}`);
      }
      entries.set(action.key, action);
      return action;
    },
    forPortalScope: (scope) =>
      Array.from(entries.values()).filter((action) => isAllowedInScope(action, scope)),
    forContext: (available) => {
      const set = new Set(available);
      return Array.from(entries.values()).filter(
        (action) =>
          !action.requiredContextTypes ||
          action.requiredContextTypes.every((type) => set.has(type)),
      );
    },
    byCategory: (category) =>
      Array.from(entries.values()).filter((action) => action.category === category),
  };
}
